import { useState } from 'react';
import { Trash2, History, Save } from 'lucide-react';
import { groupSeries } from '../lib/kHist';

const nf = (v, d = 2) => (v == null || !Number.isFinite(+v) ? '–' : (+v).toFixed(d).replace('.', ','));
const COLS = ['#dc2626', '#0e7490', '#ca8a04', '#7c3aed', '#16a34a', '#ea580c', '#475569'];
const today = () => new Date().toISOString().slice(0, 10);

// save the shown calibration result with a chosen date (e.g. for older measurements)
export function KHistSave({ t, onSave }) {
  const [date, setDate] = useState(today()), [note, setNote] = useState('');
  return (
    <div className="row khist-save" data-testid="khist-save">
      <label>{t.khDate}<input type="date" data-testid="khist-date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} /></label>
      <label>{t.khNote}<input type="text" data-testid="khist-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t.khNotePh} /></label>
      <button type="button" className="btn btn-outline btn-sm" data-testid="khist-save-btn" disabled={!date} onClick={() => onSave(date, note)}><Save size={14} /> {t.khSave}</button>
    </div>
  );
}

function Chart({ S }) {
  const all = S.flatMap((s) => s.pts), ts = all.map((p) => new Date(p.date).getTime()), t0 = Math.min(...ts), t1 = Math.max(...ts), kMax = Math.max(1, ...all.map((p) => p.k)) * 1.1;
  const X = (d) => 40 + (t1 > t0 ? ((new Date(d).getTime() - t0) / (t1 - t0)) * 300 : 150), Y = (k) => 130 - (k / kMax) * 110;
  return (
    <svg viewBox="0 0 360 150" className="khist-chart" data-testid="khist-chart">
      <line x1="40" y1="130" x2="345" y2="130" stroke="#94a3b8" /><line x1="40" y1="15" x2="40" y2="130" stroke="#94a3b8" />
      {[0, 0.5, 1].map((f) => <text key={f} x="36" y={Y(kMax * f) + 3} fontSize="9" textAnchor="end" fill="#64748b">{nf(kMax * f, 1)}</text>)}
      <text x="40" y="145" fontSize="9" fill="#64748b">{all.length ? new Date(t0).toISOString().slice(0, 10) : ''}</text>
      <text x="345" y="145" fontSize="9" textAnchor="end" fill="#64748b">{t1 > t0 ? new Date(t1).toISOString().slice(0, 10) : ''}</text>
      {S.map((s, i) => <g key={s.label}><polyline points={s.pts.map((p) => `${X(p.date)},${Y(p.k)}`).join(' ')} fill="none" stroke={COLS[i % COLS.length]} strokeWidth="2" />{s.pts.map((p) => <circle key={p.id} cx={X(p.date)} cy={Y(p.k)} r="3" fill={COLS[i % COLS.length]} />)}</g>)}
    </svg>
  );
}

// history of saved calibrations: chart k over time per group, increase per year, delete entries
export function KHist({ t, hist, onDel }) {
  if (!hist?.length) return <small className="muted" data-testid="khist-empty"><History size={13} /> {t.khEmpty}</small>;
  const S = groupSeries(hist);
  return (
    <div className="khist" data-testid="khist">
      <b><History size={15} /> {t.khTitle}</b>
      {S.length > 0 && <Chart S={S} />}
      <table className="cal-tab"><thead><tr><th>{t.calGroup}</th><th>{t.khFirst}</th><th>{t.khLast}</th><th>{t.khRate}</th></tr></thead>
        <tbody>{S.map((s, i) => <tr key={s.label} data-testid={`khist-series-${i}`}><td><i className="khist-dot" style={{ background: COLS[i % COLS.length] }} /> {s.label}</td><td>{s.pts[0].date}: {nf(s.pts[0].k)}</td><td>{s.pts.at(-1).date}: {nf(s.pts.at(-1).k)}</td><td data-testid={`khist-rate-${i}`}>{s.rate == null ? '–' : `${s.rate >= 0 ? '+' : ''}${nf(s.rate, 3)} mm/a`}</td></tr>)}</tbody></table>
      <small className="muted">{t.khRateHint}</small>
      <ul className="khist-list">{[...hist].sort((a, b) => b.date.localeCompare(a.date)).map((e) => (
        <li key={e.id} data-testid={`khist-entry-${e.id}`}>{e.date} · {t[`calMode_${e.mode}`]} · {e.meas.length} {t.khMeas}{e.note ? ` · ${e.note}` : ''}
          <button type="button" className="icon-btn" title={t.khDel} data-testid={`khist-del-${e.id}`} onClick={() => onDel(e.id)}><Trash2 size={13} /></button></li>))}</ul>
    </div>
  );
}
